/**
 * useReagentDates — per-kit "bottle opened on" date tracker (bonus F).
 *
 * Persisted on the Pi (namespace `test-kit-reagent-dates`) via
 * `useServerBackedState`. The legacy localStorage copy
 * (`reefapp.testKits.reagentDates`) seeds the default and is never
 * deleted. Storage format: JSON object mapping kit slugs to ISO date
 * strings.
 */
import { useState, useCallback, useRef } from 'react';
import { useServerBackedState } from '../hooks/useServerBackedState';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_STORAGE_KEY = 'reefapp.testKits.reagentDates';

/** `/api/userdata` namespace holding the reagent dates on the Pi. */
export const REAGENT_DATES_NAMESPACE = 'test-kit-reagent-dates';

export type ReagentDates = Readonly<Record<string, string>>;

function readLegacy(): ReagentDates {
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as ReagentDates;
      }
    }
  } catch {
    // corrupt — use defaults
  }
  return {};
}

export interface UseReagentDatesReturn {
  dates: ReagentDates;
  setDate(slug: string, iso: string): void;
  clearDate(slug: string): void;
  monthsSince(slug: string): number | null;
}

export function useReagentDates(): UseReagentDatesReturn {
  const [legacyDefault] = useState(readLegacy);
  const { value, setValue } = useServerBackedState<ReagentDates>(
    REAGENT_DATES_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob.
  const dates: ReagentDates =
    value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const datesRef = useRef(dates);
  datesRef.current = dates;

  const setDate = useCallback(
    (slug: string, iso: string) => {
      setValue({ ...datesRef.current, [slug]: iso });
    },
    [setValue],
  );

  const clearDate = useCallback(
    (slug: string) => {
      const next: Record<string, string> = { ...datesRef.current };
      delete next[slug];
      setValue(next);
    },
    [setValue],
  );

  const monthsSince = useCallback(
    (slug: string): number | null => {
      const iso = dates[slug];
      if (!iso) return null;
      const then = new Date(iso).getTime();
      if (isNaN(then)) return null;
      const now = Date.now();
      const diffMs = now - then;
      if (diffMs < 0) return 0;
      return diffMs / (1000 * 60 * 60 * 24 * 30.4375); // avg months
    },
    [dates],
  );

  return { dates, setDate, clearDate, monthsSince };
}
